<template>
    <div class="header-editor">
        <label>{{ $t('whatsapp_business_api.header') }} • {{ headerLabel }}</label>
        <a-select 
            v-model="localHeaderFormat" 
            style="width: 200px; margin-bottom: 8px"
            :disabled="isProductHeader"
            @change="onFormatChange"
        >
            <a-select-option value="NONE" v-if="!isProductHeader">{{ $t('whatsapp_business_api.headerNone') }}</a-select-option>
            <a-select-option value="TEXT" v-if="!isProductHeader && !isLimitedTimeOffer">{{ $t('whatsapp_business_api.headerTextOption') }}</a-select-option>
            <a-select-option value="IMAGE" v-if="!isProductHeader && !isCallPermission && !isCouponCodeTemplate">{{ $t('whatsapp_business_api.image') }}</a-select-option>
            <a-select-option value="VIDEO" v-if="!isProductHeader && !isCallPermission && !isCouponCodeTemplate">{{ $t('whatsapp_business_api.video') }}</a-select-option>
            <a-select-option value="DOCUMENT" v-if="!isProductHeader && !isCouponCodeTemplate && !isLimitedTimeOffer && !isCallPermission">{{ $t('whatsapp_business_api.headerDocument') }}</a-select-option>
            <a-select-option value="PRODUCT" v-if="isProductHeader">{{ $t('whatsapp_business_api.headerProduct') }}</a-select-option>
        </a-select>
        <div v-if="isProductHeader" class="product-header-info">
            <a-icon type="info-circle" />
            <span>{{ $t('whatsapp_business_api.productHeaderAutoSet') }}</span>
        </div>
        
        <!-- Text Header -->
        <template v-if="headerFormat === 'TEXT'">
            <a-input
                :value="headerText"
                @input="$emit('update:headerText', $event.target.value)"
                :placeholder="$t('whatsapp_business_api.headerTextPlaceholder')"
                :maxLength="60"
            />
            <span class="char-count">{{ (headerText || "").length }}/60</span>

            <!-- Header Variables -->
            <div v-if="headerVariables.length" class="variables-section">
                <div class="variables-header">
                    <span>{{ $t("whatsapp_business_api.variables") }}</span>
                    <span>{{ $t("whatsapp_business_api.defaultVariableName") }}</span>
                </div>
                <div v-for="(variable, i) of headerVariables" :key="i" class="variable-row">
                    <a-input :value="variable.variable" disabled />
                    <a-input
                        :value="variable.example"
                        @input="updateHeaderVariableExample(i, $event.target.value)"
                        :placeholder="$t('whatsapp_business_api.example')"
                    />
                </div>
            </div>
        </template>
        
        <!-- Media Upload Area -->
        <div v-if="isMediaHeader" class="media-upload-section">
            <div class="media-preview-box" v-if="media">
                <img v-if="headerFormat === 'IMAGE'" :src="media.data" :alt="$t('whatsapp_business_api.previewAlt')" />
                <video v-else-if="headerFormat === 'VIDEO'" :src="media.data" controls />
                <div v-else class="document-box">
                    <a-icon type="file-pdf" />
                    <span>{{ media.name }}</span>
                </div>
                <a-button type="link" class="remove-media-btn" @click="$emit('remove-media')">
                    <a-icon type="delete" />
                </a-button>
                <div class="upload-status" v-if="uploading">
                    <a-spin size="small" /> {{ $t('whatsapp_business_api.uploadingToMeta') }}
                </div>
                <div class="upload-success" v-else-if="exampleMedia">
                    <a-icon type="check-circle" /> {{ $t('whatsapp_business_api.uploadedSuccessfully') }}
                </div>
            </div>
            <div class="media-upload-box" v-else>
                <input
                    type="file"
                    :accept="getMediaAcceptType()"
                    @change="$emit('file-upload', $event)"
                    class="file-input"
                />
                <div class="upload-content">
                    <a-icon type="cloud-upload" class="upload-icon" />
                    <div class="upload-text">{{ $t('whatsapp_business_api.dragDropUpload') }}</div>
                    <div class="upload-link">{{ $t('whatsapp_business_api.orChooseFiles') }}</div>
                </div>
            </div>
            <div class="media-hint">
                <b>{{ $t('whatsapp_business_api.mediaHintNote') }}</b> {{ $t('whatsapp_business_api.mediaHintIntro') }}
                <ul>
                    <li v-if="headerFormat === 'IMAGE'">{{ $t('whatsapp_business_api.mediaHintImage') }}</li>
                    <li v-if="headerFormat === 'VIDEO'">{{ $t('whatsapp_business_api.mediaHintVideo') }}</li>
                    <li v-if="headerFormat === 'DOCUMENT'">{{ $t('whatsapp_business_api.mediaHintDocument') }}</li>
                </ul>
            </div>
        </div>
    </div>
</template>

<script>
import Vue from "vue";

export default Vue.extend({
    name: "HeaderEditor",
    props: {
        headerFormat: { type: String, default: "NONE" },
        headerText: { type: String, default: "" },
        headerVariables: { type: Array, default: () => [] },
        media: { type: Object, default: null },
        uploading: { type: Boolean, default: false },
        exampleMedia: { type: String, default: null },
        isProductHeader: { type: Boolean, default: false },
        templateType: { type: String, default: "DEFAULT" },
    },
    data() {
        return {
            localHeaderFormat: this.headerFormat,
        };
    },
    computed: {
        headerLabel() {
            if (this.templateType === 'LIMITED_TIME_OFFER') return this.$t('whatsapp_business_api.headerLabelRecommended');
            if (this.templateType === 'COUPON_CODE') return this.$t('whatsapp_business_api.headerLabelOptionalTextOnly');
            if (this.templateType === 'CALL_PERMISSION') return this.$t('whatsapp_business_api.headerLabelOptionalTextOnly');
            if (this.isProductHeader) return this.$t('whatsapp_business_api.headerLabelRequiredProduct');
            return this.$t('whatsapp_business_api.headerLabelOptional');
        },
        isMediaHeader() {
            return ["IMAGE", "VIDEO", "DOCUMENT"].includes(this.headerFormat);
        },
        isCouponCodeTemplate() {
            return this.templateType === 'COUPON_CODE';
        },
        isLimitedTimeOffer() {
            return this.templateType === 'LIMITED_TIME_OFFER';
        },
        isCallPermission() {
            return this.templateType === 'CALL_PERMISSION';
        },
    },
    watch: {
        headerFormat(newVal) {
            this.localHeaderFormat = newVal;
        },
    },
    methods: {
        onFormatChange(value) {
            this.$emit('update:headerFormat', value);
        },
        updateHeaderVariableExample(index, value) {
            this.$emit('update-header-variable-example', { index, value });
        },
        getMediaAcceptType() {
            if (this.headerFormat === "IMAGE") return "image/jpeg, image/png";
            if (this.headerFormat === "VIDEO") return "video/mp4";
            if (this.headerFormat === "DOCUMENT") return "application/pdf";
            return "";
        },
    },
});
</script>

<style scoped lang="scss">
.header-editor {
    label {
        display: block;
        margin-bottom: 8px;
        font-weight: 500;

        .required {
            color: #ff4d4f;
        }
    }

    .char-count {
        font-size: 12px;
        color: #999;
        display: block;
        margin-top: 4px;
    }

    .product-header-info {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 12px;
        background: #e6f7ff;
        border-radius: 6px;
        font-size: 13px;
        color: #0050b3;
        margin-top: 8px;

        .anticon {
            font-size: 16px;
        }
    }

    .variables-section {
        margin-top: 16px;
        padding: 12px;
        background: #fafafa;
        border-radius: 6px;
        border: 1px solid #e8e8e8;

        .variables-header {
            display: flex;
            gap: 12px;
            margin-bottom: 8px;
            font-weight: 500;
            font-size: 13px;
            color: #666;

            span {
                flex: 1;
            }
        }

        .variable-row {
            display: flex;
            gap: 12px;
            margin-bottom: 8px;

            &:last-child {
                margin-bottom: 0;
            }

            .ant-input {
                flex: 1;
            }

            .ant-input:disabled {
                background: #f0f0f0;
                color: #1890ff;
                font-weight: 500;
            }
        }
    }

    .media-upload-section {
        margin-top: 12px;

        .media-preview-box {
            position: relative;
            border: 1px solid #e8e8e8;
            border-radius: 8px;
            overflow: hidden;
            background: #fafafa;
            padding: 12px;

            img, video {
                width: 100%;
                max-height: 200px;
                object-fit: cover;
                border-radius: 6px;
            }

            .document-box {
                display: flex;
                align-items: center;
                gap: 12px;
                padding: 20px;
                background: white;
                border-radius: 6px;

                .anticon {
                    font-size: 32px;
                    color: #ff4d4f;
                }
            }

            .remove-media-btn {
                position: absolute;
                top: 20px;
                right: 20px;
                background: rgba(0, 0, 0, 0.5);
                color: white;
                border-radius: 50%;
                width: 32px;
                height: 32px;
                display: flex;
                align-items: center;
                justify-content: center;

                &:hover {
                    background: rgba(255, 77, 79, 0.8);
                }
            }

            .upload-status, .upload-success {
                margin-top: 12px;
                padding: 8px 12px;
                background: white;
                border-radius: 6px;
                font-size: 13px;
                display: flex;
                align-items: center;
                gap: 8px;
            }

            .upload-success {
                color: #52c41a;
            }
        }

        .media-upload-box {
            position: relative;
            border: 2px dashed #d9d9d9;
            border-radius: 8px;
            padding: 40px 20px;
            text-align: center;
            background: #fafafa;
            cursor: pointer;
            transition: all 0.3s;

            &:hover {
                border-color: #1890ff;
                background: #f0f7ff;
            }

            .file-input {
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                opacity: 0;
                cursor: pointer;
            }

            .upload-content {
                pointer-events: none;

                .upload-icon {
                    font-size: 48px;
                    color: #1890ff;
                }

                .upload-text {
                    font-size: 14px;
                    color: #333;
                    margin-top: 12px;
                }

                .upload-link {
                    font-size: 13px;
                    color: #1890ff;
                    margin-top: 4px;
                }
            }
        }

        .media-hint {
            margin-top: 12px;
            padding: 12px;
            background: #fff7e6;
            border-radius: 6px;
            font-size: 12px;
            color: #666;

            ul {
                margin: 8px 0 0 16px;
                padding: 0;

                li {
                    margin-bottom: 4px;
                }
            }
        }
    }
}
</style>
